import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Paper,
  FormControlLabel,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import errorService from '@services/errorService';
import {
  useTable,
  SortingState,
} from '@tanstack/react-table';
import { features } from '../components/common/tableFeatures';
import { DataTable } from '../components/common/DataTable';
import { createClientVersionColumns } from '../components/client-updates/clientVersionColumns';
import DeployDialog from '../components/deployments/DeployDialog';
import RecentDeployments from '../components/deployments/RecentDeployments';
import {
  useGetClientVersionsQuery,
  useUploadClientVersionMutation,
  useDeleteClientVersionMutation,
} from '@store/api/clientUpdatesApi';

const ClientUpdatesPage: React.FC = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  // Upload form state
  const [uploadVersion, setUploadVersion] = useState('');
  const [uploadChangelog, setUploadChangelog] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadCritical, setUploadCritical] = useState(false);

  const {
    data: versions,
    isLoading: loading,
    isError: versionsIsError,
    error: versionsError,
  } = useGetClientVersionsQuery();
  const [uploadClientVersion, { isLoading: uploading }] = useUploadClientVersionMutation();
  const [deleteClientVersion] = useDeleteClientVersionMutation();

  const displayError = error ?? (versionsIsError ? errorService.toUserMessage(versionsError) : null);

  const handleUpload = async () => {
    if (!uploadFile || !uploadVersion || !uploadChangelog) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setError(null);
      await uploadClientVersion({
        version: uploadVersion,
        file: uploadFile,
        changelog: uploadChangelog,
        isCritical: uploadCritical,
      }).unwrap();
      setUploadDialogOpen(false);
      setUploadVersion('');
      setUploadChangelog('');
      setUploadFile(null);
      setUploadCritical(false);
    } catch (err) {
      setError(errorService.toUserMessage(err));
    }
  };

  const handleDelete = async (version: string) => {
    if (!confirm(`Are you sure you want to delete version ${version}?`)) {
      return;
    }

    try {
      setError(null);
      await deleteClientVersion(version).unwrap();
    } catch (err) {
      setError(errorService.toUserMessage(err));
    }
  };

  // Memoised because this is the table's `data`: rebuilding the array on every
  // render gives TanStack a new reference each time, which rebuilds the row
  // models and defeats every downstream memo.
  const sortedVersions = useMemo(
    () =>
      Object.values(versions ?? {}).sort(
        (a, b) => new Date(b.release_date).getTime() - new Date(a.release_date).getTime()
      ),
    [versions]
  );

  const versionColumns = useMemo(
    () =>
      createClientVersionColumns({
        onDeploy: setSelectedVersion,
        onDelete: handleDelete,
      }),
    // Safe to build once: the handlers only touch state setters and RTK
    // mutation triggers, all of which keep their identity across renders.
    []
  );

  const versionsTable = useTable({
    features,
    data: sortedVersions,
    columns: versionColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getRowId: (row) => row.version,
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Client Updates
        </Typography>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => setUploadDialogOpen(true)}
        >
          Upload New Version
        </Button>
      </Box>

      {displayError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {displayError}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <DataTable
          table={versionsTable}
          ariaLabel="Client versions"
          isLoading={loading}
          emptyMessage="No client versions available. Upload your first version to get started."
          minWidth={900}
          // Deleted versions stay listed but read as inactive.
          getRowSx={(version) =>
            version.is_deleted
              ? { opacity: 0.5, bgcolor: 'action.disabledBackground' }
              : {}
          }
        />
      </Paper>

      <RecentDeployments kind="client" />

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload New Client Version</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Version"
              placeholder="1.0.0"
              value={uploadVersion}
              onChange={(e) => setUploadVersion(e.target.value)}
              helperText="Semantic version format (e.g., 1.0.0)"
              required
              fullWidth
            />
            <TextField
              label="Changelog"
              placeholder="What's new in this version..."
              value={uploadChangelog}
              onChange={(e) => setUploadChangelog(e.target.value)}
              multiline
              rows={3}
              required
              fullWidth
            />
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              fullWidth
            >
              {uploadFile ? uploadFile.name : 'Select .tar.gz File'}
              <input
                type="file"
                hidden
                accept=".tar.gz,.tgz"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </Button>
            <FormControlLabel
              control={
                <Checkbox
                  checked={uploadCritical}
                  onChange={(e) => setUploadCritical(e.target.checked)}
                />
              }
              label="Mark as critical update"
            />
            {uploading && <LinearProgress />}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !uploadFile || !uploadVersion || !uploadChangelog}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <DeployDialog
        key={selectedVersion ?? ''}
        kind="client"
        version={selectedVersion}
        onClose={() => setSelectedVersion(null)}
        notice={
          <Alert severity="info">
            Each selected device gets one update command. Follow their progress on the
            deployment page that opens next.
          </Alert>
        }
      />
    </Box>
  );
};

export default ClientUpdatesPage;
